import { useMemo } from 'react';

import dayjs from 'dayjs';
import { useSearchParams } from 'react-router';

import type { Gec_Events_Bool_Exp } from '../../gql/graphql';

function useBuildGraphQLVariables() {
  const [urlFilters] = useSearchParams();

  return useMemo(() => {
    const get = (key: string) => urlFilters.get(key);
    const pipe = (key: string) => get(key)?.split('|') ?? [];
    const ids = (key: string) => convertOptionTypeArrayToIds(pipe(key));

    const filters: Gec_Events_Bool_Exp[] = [];

    const add = (key: string, filter: Gec_Events_Bool_Exp) => {
      if (get(key)) filters.push(filter);
    };

    add('eventName', {
      title: { _ilike: `%${get('eventName')}%` },
    });

    const startDate = get('startDate');
    const endDate = get('endDate');

    if (startDate || endDate) {
      const from = startDate ?? dayjs(endDate).subtract(1, 'year').format('YYYY-MM-DD');
      const to = endDate
        ? dayjs(endDate).endOf('day').format('YYYY-MM-DDTHH:mm:ss')
        : dayjs(startDate).subtract(1, 'year').format('YYYY-MM-DDTHH:mm:ss');

      filters.push({
        _or: [
          {
            start_date_time_utc: { _lte: to },
            end_date_time_utc: { _gte: from },
          },
          { start_date_time_utc: { _gte: from, _lte: to } },
          { end_date_time_utc: { _gte: from, _lte: to } },
          {
            start_date_time_utc: { _eq: from },
            end_date_time_utc: { _eq: to },
          },
          { is_recurring: { _eq: true } },
        ],
      });
    }

    add('hostingType', {
      hosting_type: { _in: get('hostingType')?.split(',') ?? [] },
    });

    add('internalLocation', {
      offices_in_events: {
        office_id: { _in: pipe('internalLocation') },
      },
    });

    add('externalLocation', {
      external_office: { _ilike: `%${get('externalLocation')}%` },
    });

    if (get('officeVisibility')) {
      filters.push({
        _or: [
          {
            office_visibility_in_events: {
              office_id: { _in: pipe('officeVisibility') },
            },
          },
        ],
      });
    }

    const idFilters: Record<string, (values: number[]) => Gec_Events_Bool_Exp> = {
      practice: (values) => ({
        practice_groups_in_event: {
          practice_groups: { id: { _in: values } },
        },
      }),
      department: (values) => ({
        departments_in_event: {
          departments: { id: { _in: values } },
        },
      }),
      audience: (values) => ({
        audience_in_events: {
          audience: { id: { _in: values } },
        },
      }),
      eventType: (values) => ({
        event_type: { id: { _in: values } },
      }),
      eventFormat: (values) => ({
        event_format: { id: { _in: values } },
      }),
      industries: (values) => ({
        industries_in_event: {
          industries: { id: { _in: values } },
        },
      }),
      firmInitiatives: (values) => ({
        firm_initiatives_in_event: {
          firm_initiatives: { id: { _in: values } },
        },
      }),
    };

    Object.entries(idFilters).forEach(([key, buildFilter]) => {
      if (get(key)) filters.push(buildFilter(ids(key)));
    });

    return { _and: filters };
  }, [urlFilters]);
}

export default useBuildGraphQLVariables;

const convertOptionTypeArrayToIds = (options: string[] | undefined) => {
  const optionIds: number[] = [];
  options?.forEach((option) => {
    optionIds.push(Number.parseInt(option.split(',')[0]));
  });
  return optionIds;
};
